import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import PostCard from '../components/PostCard';
import FloatingBubbles from '../components/FloatingBubbles';
import axios from 'axios';

const POST_TYPES = [
  { value: 'post', label: 'Post', color: '#4A5568', bg: '#F7F9FC' },
  { value: 'analyse', label: 'Analyse', color: '#2B6CB0', bg: '#EBF4FF' },
  { value: 'projet', label: 'Projet', color: '#276749', bg: '#F0FFF4' },
  { value: 'decouverte', label: 'Découverte', color: '#9B2C2C', bg: '#FFF5F5' },
];

export default function Feed() {
  const { user, token, API } = useAuth();
  const [posts, setPosts] = useState([]);
  const [content, setContent] = useState('');
  const [postType, setPostType] = useState('post');
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [activeTab, setActiveTab] = useState('forYou');
  const [myProjects, setMyProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [showProjectPicker, setShowProjectPicker] = useState(false);
  const [projectSearch, setProjectSearch] = useState('');

  // Media state
  const [mediaPreview, setMediaPreview] = useState(null);
  const [mediaUrl, setMediaUrl] = useState(null);
  const [mediaType, setMediaType] = useState(null);
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const mediaInputRef = useRef(null);

  const fetchPosts = useCallback(async () => {
    setFetching(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const url = activeTab === 'following' && token ? `${API}/posts/following` : `${API}/posts`;
      const res = await axios.get(url, { headers });
      setPosts(res.data);
    } catch (err) { console.error(err); }
    finally { setFetching(false); }
  }, [API, token, activeTab]);

  const fetchMyProjects = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/projects`, { headers: { Authorization: `Bearer ${token}` } });
      setMyProjects(res.data.filter(p => p.members.some(m => m.user.username === user?.username)));
    } catch (err) { console.error(err); }
  }, [API, token, user]);

  useEffect(() => { fetchPosts(); }, [fetchPosts]);
  useEffect(() => { fetchMyProjects(); }, [fetchMyProjects]);

  useEffect(() => {
    if (postType === 'projet') setShowProjectPicker(true);
    else { setShowProjectPicker(false); setSelectedProject(null); }
  }, [postType]);

  const handleMediaSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const preview = URL.createObjectURL(file);
    setMediaPreview(preview);
    setUploadingMedia(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await axios.post(`${API}/posts/upload-media`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      setMediaUrl(res.data.url);
      setMediaType(res.data.media_type);
    } catch (err) { console.error(err); setMediaPreview(null); }
    finally { setUploadingMedia(false); }
  };

  const removeMedia = () => {
    setMediaPreview(null);
    setMediaUrl(null);
    setMediaType(null);
    if (mediaInputRef.current) mediaInputRef.current.value = '';
  };

  const handlePost = async (e) => {
    e.preventDefault();
    if (!content.trim() && !mediaUrl) return;
    setLoading(true);
    try {
      await axios.post(`${API}/posts`, {
        content,
        post_type: postType,
        project_id: selectedProject?.id || null,
        media_url: mediaUrl,
        media_type: mediaType,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setContent('');
      setSelectedProject(null);
      setPostType('post');
      removeMedia();
      fetchPosts();
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  return (
    <>
      <FloatingBubbles />
      <div style={{ position: 'relative', zIndex: 1 }}>

        {/* Hero */}
        <div style={{ background: 'rgba(235,244,255,0.85)', backdropFilter: 'blur(4px)', padding: '48px 20px 0', textAlign: 'center', borderBottom: '1px solid rgba(190,227,248,0.5)' }}>
          <div className="fade-up" style={{ marginBottom: '12px' }}>
            <span style={{ background: 'rgba(43,108,176,0.1)', color: 'var(--blue)', fontSize: '11px', fontWeight: 600, padding: '5px 16px', borderRadius: '20px', letterSpacing: '1px' }}>BIENVENUE SUR AGORA</span>
          </div>
          <h1 className="fade-up" style={{ fontSize: '38px', fontWeight: 800, color: 'var(--dark)', letterSpacing: '-1.5px', marginBottom: '12px', lineHeight: 1.1 }}>
            {user ? `Bonjour, ${user.username} 👋` : 'Trouve ta tribu.'}
          </h1>
          <p className="fade-up" style={{ fontSize: '16px', color: 'var(--text)', maxWidth: '420px', margin: '0 auto 28px', lineHeight: 1.7 }}>
            {user ? 'Partage ce qui te passionne avec la communauté.' : 'Rejoins des milliers de passionnés qui partagent, construisent et collaborent.'}
          </p>
          {user && (
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              {[{ key: 'forYou', label: 'Pour toi' }, { key: 'following', label: 'Abonnements' }].map(tab => (
                <button key={tab.key} onClick={() => setActiveTab(tab.key)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '12px 28px', fontSize: '14px', fontWeight: 600, color: activeTab === tab.key ? 'var(--blue)' : 'var(--muted)', borderBottom: `2px solid ${activeTab === tab.key ? 'var(--blue)' : 'transparent'}`, transition: 'all .2s' }}>
                  {tab.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Feed */}
        <div style={{ maxWidth: '680px', margin: '0 auto', padding: '32px 20px 48px' }}>

          {/* Composer */}
          {user ? (
            <div className="fade-up" style={{ background: 'rgba(255,255,255,0.88)', backdropFilter: 'blur(12px)', borderRadius: 'var(--radius)', padding: '24px', border: '1px solid rgba(190,227,248,0.7)', boxShadow: 'var(--shadow)', marginBottom: '28px' }}>
              <div style={{ display: 'flex', gap: '12px' }}>
                <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: 'var(--blue-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '13px', color: 'var(--blue)', flexShrink: 0 }}>
                  {user.username.slice(0, 2).toUpperCase()}
                </div>
                <form onSubmit={handlePost} style={{ flex: 1 }}>
                  <textarea
                    placeholder="Partage quelque chose qui te passionne..."
                    value={content}
                    onChange={e => setContent(e.target.value)}
                    rows={3}
                    style={{ width: '100%', border: '1.5px solid var(--blue-border)', borderRadius: '12px', padding: '12px 16px', fontSize: '14px', resize: 'none', outline: 'none', lineHeight: 1.7, transition: 'border-color .2s', background: 'rgba(250,252,255,0.9)' }}
                    onFocus={e => e.target.style.borderColor = 'var(--blue)'}
                    onBlur={e => e.target.style.borderColor = 'var(--blue-border)'}
                  />

                  {/* Media preview */}
                  {mediaPreview && (
                    <div style={{ marginTop: '10px', position: 'relative', borderRadius: '12px', overflow: 'hidden', background: 'var(--bg)', border: '1px solid var(--border)' }}>
                      {uploadingMedia && (
                        <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2 }}>
                          <span style={{ color: '#fff', fontSize: '13px', fontWeight: 600 }}>Upload en cours...</span>
                        </div>
                      )}
                      {mediaType === 'video' ? (
                        <video src={mediaPreview} style={{ width: '100%', maxHeight: '300px', objectFit: 'cover' }} controls />
                      ) : (
                        <img src={mediaPreview} alt="preview" style={{ width: '100%', maxHeight: '300px', objectFit: 'cover' }} />
                      )}
                      <button type="button" onClick={removeMedia} style={{ position: 'absolute', top: '8px', right: '8px', background: 'rgba(0,0,0,0.6)', color: '#fff', border: 'none', width: '28px', height: '28px', borderRadius: '50%', cursor: 'pointer', fontSize: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3 }}>×</button>
                    </div>
                  )}

                  {/* Project picker */}
                  {showProjectPicker && (
                    <div style={{ marginTop: '10px', padding: '12px', background: 'var(--blue-bg)', borderRadius: '12px', border: '1px solid var(--blue-border)' }}>
                      <p style={{ fontSize: '12px', fontWeight: 600, color: 'var(--blue)', marginBottom: '8px' }}>🔗 Lier à un projet (optionnel)</p>
                      {myProjects.length === 0 ? (
                        <p style={{ fontSize: '12px', color: 'var(--muted)' }}>Tu n'as pas encore de projet. <Link to="/projects" style={{ color: 'var(--blue)', fontWeight: 600 }}>En créer un →</Link></p>
                      ) : (
                        <div>
                          <input value={projectSearch} onChange={e => setProjectSearch(e.target.value)} placeholder="Rechercher un projet..."
                            style={{ width: '100%', padding: '7px 12px', border: '1.5px solid var(--blue-border)', borderRadius: '20px', fontSize: '12px', outline: 'none', marginBottom: '8px', background: '#fff' }}
                            onFocus={e => e.target.style.borderColor = 'var(--blue)'} onBlur={e => e.target.style.borderColor = 'var(--blue-border)'}
                          />
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', maxHeight: '100px', overflowY: 'auto' }}>
                            <button type="button" onClick={() => setSelectedProject(null)} style={{ background: !selectedProject ? 'var(--blue)' : '#fff', color: !selectedProject ? '#fff' : 'var(--muted)', border: `1px solid ${!selectedProject ? 'var(--blue)' : 'var(--border)'}`, padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}>Aucun</button>
                            {myProjects.filter(p => p.title.toLowerCase().includes(projectSearch.toLowerCase())).map(p => (
                              <button key={p.id} type="button" onClick={() => setSelectedProject(p)} style={{ background: selectedProject?.id === p.id ? 'var(--blue)' : '#fff', color: selectedProject?.id === p.id ? '#fff' : 'var(--dark)', border: `1px solid ${selectedProject?.id === p.id ? 'var(--blue)' : 'var(--border)'}`, padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: 600, cursor: 'pointer', transition: 'all .2s' }}>
                                {p.emoji || '🚀'} {p.title}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Actions bar */}
                  <div style={{ display: 'flex', gap: '8px', marginTop: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
                    {POST_TYPES.map(t => (
                      <button key={t.value} type="button" onClick={() => setPostType(t.value)} style={{ background: postType === t.value ? t.bg : 'transparent', color: postType === t.value ? t.color : 'var(--muted)', border: `1.5px solid ${postType === t.value ? t.color : 'var(--border)'}`, padding: '5px 14px', borderRadius: '20px', fontSize: '12px', fontWeight: 600, cursor: 'pointer', transition: 'all .2s' }}>
                        {t.label}
                      </button>
                    ))}

                    {/* Media upload button */}
                    <input ref={mediaInputRef} type="file" accept="image/*,video/*" onChange={handleMediaSelect} style={{ display: 'none' }} />
                    <button type="button" onClick={() => mediaInputRef.current.click()} disabled={uploadingMedia} style={{ background: mediaPreview ? 'var(--blue-bg)' : 'transparent', color: mediaPreview ? 'var(--blue)' : 'var(--muted)', border: `1.5px solid ${mediaPreview ? 'var(--blue)' : 'var(--border)'}`, padding: '5px 14px', borderRadius: '20px', fontSize: '12px', fontWeight: 600, cursor: 'pointer', transition: 'all .2s', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      📷 Photo/Vidéo
                    </button>

                    <button type="submit" disabled={loading || uploadingMedia || (!content.trim() && !mediaUrl)} style={{ marginLeft: 'auto', background: (content.trim() || mediaUrl) && !uploadingMedia ? 'var(--blue)' : 'var(--border)', color: (content.trim() || mediaUrl) && !uploadingMedia ? '#fff' : 'var(--muted)', border: 'none', padding: '8px 22px', borderRadius: '24px', fontSize: '13px', fontWeight: 600, cursor: (content.trim() || mediaUrl) && !uploadingMedia ? 'pointer' : 'default', transition: 'all .2s', boxShadow: (content.trim() || mediaUrl) ? '0 4px 12px rgba(43,108,176,0.25)' : 'none' }}>
                      {loading ? 'Publication...' : uploadingMedia ? 'Upload...' : 'Publier →'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          ) : (
            <div className="fade-up" style={{ background: 'var(--blue)', borderRadius: 'var(--radius)', padding: '32px', textAlign: 'center', boxShadow: '0 8px 32px rgba(43,108,176,0.25)', marginBottom: '28px' }}>
              <h2 style={{ fontSize: '20px', fontWeight: 700, color: '#fff', marginBottom: '8px' }}>Rejoins Agora</h2>
              <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.75)', marginBottom: '20px' }}>Crée un compte pour partager et interagir avec la communauté.</p>
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                <Link to="/register" style={{ background: '#fff', color: 'var(--blue)', padding: '10px 24px', borderRadius: '24px', fontSize: '14px', fontWeight: 700 }}>S'inscrire →</Link>
                <Link to="/login" style={{ background: 'rgba(255,255,255,0.15)', color: '#fff', padding: '10px 24px', borderRadius: '24px', fontSize: '14px', fontWeight: 600, border: '1px solid rgba(255,255,255,0.3)' }}>Connexion</Link>
              </div>
            </div>
          )}

          {/* Posts */}
          {fetching ? (
            <div style={{ textAlign: 'center', padding: '60px', color: 'var(--muted)' }}><p>Chargement...</p></div>
          ) : posts.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 20px', background: 'rgba(255,255,255,0.8)', borderRadius: 'var(--radius)', border: '1px solid var(--border)' }}>
              {activeTab === 'following' ? (
                <>
                  <div style={{ fontSize: '48px', marginBottom: '16px' }}>👥</div>
                  <p style={{ fontSize: '16px', fontWeight: 600, color: 'var(--dark)', marginBottom: '8px' }}>Ton feed abonnements est vide</p>
                  <p style={{ fontSize: '14px', color: 'var(--muted)' }}>Suis des membres pour voir leurs posts ici.</p>
                  <Link to="/search" style={{ display: 'inline-block', marginTop: '16px', background: 'var(--blue)', color: '#fff', padding: '10px 24px', borderRadius: '24px', fontSize: '14px', fontWeight: 600 }}>Trouver des membres →</Link>
                </>
              ) : (
                <>
                  <div style={{ fontSize: '48px', marginBottom: '16px' }}>🌊</div>
                  <p style={{ fontSize: '16px', fontWeight: 500, color: 'var(--muted)' }}>Sois le premier à partager quelque chose !</p>
                </>
              )}
            </div>
          ) : (
            posts.map((post, i) => (
              <div key={post.id} style={{ animation: `fadeUp 0.5s ease ${i * 0.05}s both` }}>
                <PostCard post={post} onUpdate={fetchPosts} />
              </div>
            ))
          )}
        </div>
      </div>
    </>
  );
}
